// src/pages/Cotizaciones.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo, useEffect } from "react";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { useClientes } from "../hooks/useClientes";
import { useUsuarios, useRol } from "../hooks/useRoles";
import { useNotasCotizacion, agregarNotaCotizacion } from "../hooks/useNotasCotizacion";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const ESTADOS = ["Pendiente", "Aprobada", "Rechazada", "Vencida", "Ganada", "Perdida"];
const ESTADOS_CERRADOS = ["Ganada", "Cerrada", "Aprobada", "Perdida", "Rechazada", "Vencida"];
// "Vencida" no tuvo una respuesta definitiva (a diferencia de Ganada/Perdida/Rechazada) —
// el cron que la marca automáticamente nunca dispara el modal que mueve el seguimiento al
// cliente, así que se le permite seguir agendando seguimiento directo en la cotización.
const ESTADOS_SIN_SEGUIMIENTO_PROPIO = ESTADOS_CERRADOS.filter(e => e !== "Vencida");

const ESTADO_STYLE = {
  "Pendiente":  { bg: "#E6F1FB", color: "#185FA5" },
  "Aprobada":   { bg: "#EAF3DE", color: "#3B6D11" },
  "Rechazada":  { bg: "#FCEBEB", color: "#A32D2D" },
  "Vencida":    { bg: "#FAEEDA", color: "#854F0B" },
  "Ganada":     { bg: "#E1F5EE", color: "#0F6E56" },
  "Perdida":    { bg: "#FCEBEB", color: "#A32D2D" },
};


const fmtMoney    = v => `S/ ${Number(v || 0).toLocaleString("es-PE")}`;
const fmtValor    = c => c?.moneda === "USD"
  ? `$ ${Number(c.valor || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : fmtMoney(c?.valor);

function diasHasta(fecha) {
  if (!fecha) return null;
  const [y, m, d] = String(fecha).split("-").map(Number);
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const f   = new Date(y, m - 1, d);
  return Math.round((f - hoy) / 86400000);
}

function labelDias(dias) {
  if (dias === null) return "—";
  if (dias < 0)  return `Vencido ${Math.abs(dias)}d`;
  if (dias === 0) return "Hoy";
  if (dias === 1) return "Mañana";
  return `En ${dias}d`;
}

function colorDias(dias) {
  if (dias === null) return "#94a3b8";
  if (dias < 0)  return "#DC2626";
  if (dias === 0) return "#D97706";
  return "#1D9E75";
}

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <span style={{ color: "#cbd5e1", fontSize: 9 }}> ⇅</span>;
  return <span style={{ color: "#3A3C7A", fontSize: 9 }}>{sortDir === "asc" ? " ↑" : " ↓"}</span>;
}

function diasEnEstado(c) {
  if (!c.estadoCambiadoEn) return null;
  const f = c.estadoCambiadoEn?.toDate ? c.estadoCambiadoEn.toDate() : new Date(c.estadoCambiadoEn || 0);
  return Math.round((new Date() - f) / 86400000);
}

// Sección de notas/comentarios por cotización (expandible)
function NotasSection({ cotizacionId, cotizacionNum, clienteNombre }) {
  const { notas, loading, eliminarNota } = useNotasCotizacion(cotizacionId);
  const [texto,      setTexto]      = useState("");
  const [tipo,       setTipo]       = useState("Nota");
  const [guardando,  setGuardando]  = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const guardar = async () => {
    if (!texto.trim()) return;
    setGuardando(true);
    await agregarNotaCotizacion({ cotizacionId, cotizacionNum, clienteNombre, texto, tipo });
    setTexto(""); setGuardando(false);
  };

  const TIPO_COLOR = {
    "Nota":        { bg: "#EEF2FF", color: "#3A3C7A" },
    "Seguimiento": { bg: "#E1F5EE", color: "#0F6E56" },
    "Estado":      { bg: "#FAEEDA", color: "#854F0B" },
  };

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
        Comentarios de seguimiento ({notas.length})
      </div>

      {/* Formulario agregar nota */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "flex-start" }}>
        <textarea
          value={texto} onChange={e => setTexto(e.target.value)}
          placeholder="Agregar comentario... (Ej: Llamé al cliente, confirmó interés para la próxima semana)"
          rows={2}
          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) guardar(); }}
          style={{ flex: 1, padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12, resize: "none", fontFamily: "inherit", outline: "none" }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <select value={tipo} onChange={e => setTipo(e.target.value)}
            style={{ padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, color: "#475569", background: "#fff", cursor: "pointer" }}>
            <option>Nota</option>
            <option>Seguimiento</option>
          </select>
          <button onClick={guardar} disabled={guardando || !texto.trim()} style={{
            padding: "6px 12px", background: texto.trim() ? "#3A3C7A" : "#e2e8f0",
            color: texto.trim() ? "#fff" : "#94a3b8", border: "none", borderRadius: 6,
            fontSize: 11, fontWeight: 600, cursor: texto.trim() ? "pointer" : "not-allowed",
          }}>
            {guardando ? "..." : "Guardar"}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 8 }}>Ctrl+Enter para guardar rápido</div>

      {/* Lista de notas */}
      {loading ? (
        <div style={{ color: "#94a3b8", fontSize: 11 }}>Cargando...</div>
      ) : notas.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 11, fontStyle: "italic" }}>Sin comentarios aún.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
          {notas.map(n => {
            const tc = TIPO_COLOR[n.tipo] || TIPO_COLOR["Nota"];
            const fecha = n.creadoEn?.toDate ? n.creadoEn.toDate() : n.fecha ? new Date(n.fecha) : null;
            return (
              <div key={n.id} style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 7, padding: "7px 10px", display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: tc.bg, color: tc.color }}>{n.tipo}</span>
                    <span style={{ fontSize: 9, color: "#94a3b8" }}>{fecha ? fecha.toLocaleDateString("es-PE") : n.fecha || ""}</span>
                    <span style={{ fontSize: 9, color: "#94a3b8" }}>· {n.autor?.split("@")[0] || "—"}</span>
                  </div>
                  <p style={{ fontSize: 12, color: "#1e293b", margin: 0, lineHeight: 1.4 }}>{n.texto}</p>
                </div>
                {confirmDel === n.id ? (
                  <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                    <button onClick={() => { eliminarNota(n.id); setConfirmDel(null); }} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#DC2626", color: "#fff", border: "none", cursor: "pointer" }}>Sí</button>
                    <button onClick={() => setConfirmDel(null)} style={{ fontSize: 9, padding: "2px 5px", borderRadius: 4, background: "#f1f5f9", color: "#475569", border: "none", cursor: "pointer" }}>No</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDel(n.id)} style={{ fontSize: 9, padding: "2px 5px", borderRadius: 4, background: "none", color: "#94a3b8", border: "none", cursor: "pointer", flexShrink: 0 }}>✕</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FORM_VACIO = {
  numeroCot: "", clienteId: "", clienteNombre: "", clienteEmpresa: "",
  proyecto: "", valor: "", estado: "Pendiente", responsable: "",
  proximoSeguimiento: "", notas: "",
};

const inp = { width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", outline: "none", background: "#fff", fontFamily: "inherit", boxSizing: "border-box" };
const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 };
const TH  = { textAlign: "left", fontSize: 10, fontWeight: 600, color: "#94a3b8", padding: "10px 12px", whiteSpace: "nowrap" };
const THS = { ...TH, cursor: "pointer", userSelect: "none" };

export default function Cotizaciones() {
  const { cotizaciones, loading, crearCotizacion, actualizarCotizacion, eliminarCotizacion } = useCotizaciones();
  const { clientes }  = useClientes();
  const { permisos }  = useRol();
  const { usuarios }  = useUsuarios();
  const navigate      = useNavigate();
  const location      = useLocation();

  // Auto-abrir modal pre-llenado cuando se navega desde un cliente
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const clienteId     = p.get("clienteId");
    const clienteNombre = p.get("clienteNombre");
    if (!clienteId || !clienteNombre) return;
    setForm(f => ({
      ...f,
      clienteId,
      clienteNombre:   decodeURIComponent(clienteNombre),
      clienteEmpresa:  decodeURIComponent(p.get("clienteEmpresa") || ""),
      responsable:     decodeURIComponent(p.get("responsable") || ""),
    }));
    setModoEditar(false);
    setEditCotId(null);
    setModalNueva(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [filtroEstado,      setFiltroEstado]      = useState("Todos");
  const [filtroResponsable, setFiltroResponsable] = useState("Todos");
  const [busqueda,          setBusqueda]          = useState("");
  const [pagina,            setPagina]            = useState(1);
  const [confirmDel,        setConfirmDel]        = useState(null);
  const [expandedId,        setExpandedId]        = useState(null);
  const [sortField,         setSortField]         = useState("numeroCot");
  const [sortDir,           setSortDir]           = useState("desc");
  // Edición inline de fecha seguimiento
  const [editFechaId,  setEditFechaId]  = useState(null);
  const [editFechaVal, setEditFechaVal] = useState("");

  const [modalNueva,  setModalNueva]  = useState(false);
  const [modoEditar,  setModoEditar]  = useState(false);
  const [editCotId,   setEditCotId]   = useState(null);
  const [form,        setForm]        = useState(FORM_VACIO);
  const [modalComent,            setModalComent]            = useState(null);
  const [comentario,             setComentario]             = useState("");
  const [proximoContactoCli,     setProximoContactoCli]     = useState("");
  const [editNumId,   setEditNumId]   = useState(null);
  const [editNumVal,  setEditNumVal]  = useState("");

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "creadoEn" ? "desc" : "asc"); }
  };

  const responsables = useMemo(() => {
    const set = new Set(cotizaciones.map(c => c.responsable).filter(Boolean));
    return [...set].sort();
  }, [cotizaciones]);

  const filtradas = useMemo(() => {
    let lista = [...cotizaciones];
    if (filtroEstado !== "Todos") lista = lista.filter(c => c.estado === filtroEstado);
    if (filtroResponsable !== "Todos") lista = lista.filter(c => c.responsable === filtroResponsable);
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(c =>
        c.clienteNombre?.toLowerCase().includes(b) ||
        c.clienteEmpresa?.toLowerCase().includes(b) ||
        c.numeroCot?.toLowerCase().includes(b) ||
        c.proyecto?.toLowerCase().includes(b)
      );
    }
    return lista;
  }, [cotizaciones, filtroEstado, filtroResponsable, busqueda]);

  const filas = useMemo(() => (
    [...filtradas].sort((a, b) => {
      let va, vb;
      if (sortField === "valor") { va = Number(a.valor) || 0; vb = Number(b.valor) || 0; }
      else if (sortField === "estado") { va = ESTADOS.indexOf(a.estado); vb = ESTADOS.indexOf(b.estado); }
      else if (sortField === "seguimiento") { va = a.proximoSeguimiento || "9999"; vb = b.proximoSeguimiento || "9999"; }
      else if (sortField === "numeroCot") { va = a.numeroCot || ""; vb = b.numeroCot || ""; }
      else {
        const fa = a.creadoEn?.toDate ? a.creadoEn.toDate() : new Date(a.creadoEn || 0);
        const fb = b.creadoEn?.toDate ? b.creadoEn.toDate() : new Date(b.creadoEn || 0);
        va = fa.getTime(); vb = fb.getTime();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    })
  ), [filtradas, sortField, sortDir]);

  const kpis = useMemo(() => {
    const porEstado = {}; const porEstadoValor = {};
    ESTADOS.forEach(e => { porEstado[e] = 0; porEstadoValor[e] = 0; });
    cotizaciones.forEach(c => {
      const e = c.estado;
      if (e in porEstado) { porEstado[e]++; porEstadoValor[e] += Number(c.valor) || 0; }
      else if (e === "Cerrada") { porEstado["Ganada"]++; porEstadoValor["Ganada"] += Number(c.valor) || 0; }
    });
    const pipelineVal = porEstadoValor["Pendiente"] + porEstadoValor["Aprobada"];
    const pipelineCnt = porEstado["Pendiente"] + porEstado["Aprobada"];
    const ganadas     = (porEstado["Ganada"] || 0) + (porEstado["Aprobada"] || 0);
    const terminados  = ["Ganada","Aprobada","Perdida","Rechazada","Vencida"].reduce((s, e) => s + (porEstado[e] || 0), 0);
    const tasaCierre  = terminados > 0 ? ((ganadas / terminados) * 100).toFixed(1) : "0.0";
    const ticketProm  = porEstado["Ganada"] > 0 ? Math.round(porEstadoValor["Ganada"] / porEstado["Ganada"]) : 0;
    const totalValor  = cotizaciones.reduce((s, c) => s + (Number(c.valor) || 0), 0);
    return { porEstado, porEstadoValor, pipelineVal, pipelineCnt, terminados, tasaCierre, ticketProm, totalValor };
  }, [cotizaciones]);

  const exportarExcel = () => {
    const data = filas.map(c => ({
      "N° Cotización":     c.numeroCot || "",
      "Cliente":           c.clienteNombre || "",
      "Empresa":           c.clienteEmpresa || "",
      "Proyecto":          c.proyecto || "",
      "Estado":            c.estado || "",
      "Valor":             Number(c.valor) || 0,
      "Moneda":            c.moneda || "PEN",
      "Responsable":       c.responsable || "",
      "Próx. Seguimiento": c.proximoSeguimiento || "",
      "Notas":             c.notas || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Array(9).fill({ wch: 22 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cotizaciones");
    XLSX.writeFile(wb, `Cotizaciones_${new Date().toLocaleDateString("es-PE").replace(/\//g, "-")}.xlsx`);
  };

  const handleCambiarEstado = (cot, nuevoEstado) => {
    if (nuevoEstado === cot.estado) return;
    if (["Ganada", "Aprobada", "Perdida", "Rechazada"].includes(nuevoEstado)) {
      setComentario("");
      setProximoContactoCli("");
      setModalComent({ id: cot.id, nuevoEstado, nombre: cot.clienteNombre, numeroCot: cot.numeroCot, clienteId: cot.clienteId });
    } else {
      actualizarCotizacion(cot.id, { estado: nuevoEstado });
    }
  };

  const confirmarComent = async () => {
    if (!modalComent) return;
    await actualizarCotizacion(modalComent.id, { estado: modalComent.nuevoEstado, comentarioHistorial: comentario });
    // Si se puso fecha de próximo contacto → actualizar el cliente en agenda
    if (proximoContactoCli && modalComent.clienteId) {
      try {
        await updateDoc(doc(db, "clientes", modalComent.clienteId), { proximoSeguimiento: proximoContactoCli });
        toast.success("📅 Agenda del cliente actualizada");
      } catch (e) { console.error("Error updating cliente:", e); }
    }
    setModalComent(null); setComentario(""); setProximoContactoCli("");
  };

  const handleClienteChange = (clienteId) => {
    const c = clientes.find(c => c.id === clienteId);
    setForm(f => ({ ...f, clienteId, clienteNombre: c?.nombre || "", clienteEmpresa: c?.empresa || "" }));
  };

  const abrirModalNueva = () => { setForm(FORM_VACIO); setModoEditar(false); setEditCotId(null); setModalNueva(true); };
  const abrirModalEditar = (cot) => {
    setForm({
      numeroCot: cot.numeroCot || "", clienteId: cot.clienteId || "",
      clienteNombre: cot.clienteNombre || "", clienteEmpresa: cot.clienteEmpresa || "",
      proyecto: cot.proyecto || "", valor: cot.valor || "", estado: cot.estado || "Pendiente",
      responsable: cot.responsable || "", proximoSeguimiento: cot.proximoSeguimiento || "", notas: cot.notas || "",
    });
    setModoEditar(true); setEditCotId(cot.id); setModalNueva(true);
  };

  const handleGuardar = async () => {
    if (!form.clienteNombre) { alert("Selecciona un cliente"); return; }
    if (modoEditar && editCotId) await actualizarCotizacion(editCotId, form);
    else await crearCotizacion(form);
    setModalNueva(false); setForm(FORM_VACIO); setModoEditar(false); setEditCotId(null);
  };

  const guardarNumero = async (id) => { await actualizarCotizacion(id, { numeroCot: editNumVal }); setEditNumId(null); };

  if (loading) return <Cargando />;

  const hayFiltros      = busqueda || filtroEstado !== "Todos" || filtroResponsable !== "Todos";
  const POR_PAGINA      = 25;
  const totalPaginasCot = Math.max(1, Math.ceil(filas.length / POR_PAGINA));
  const paginaValida    = Math.min(pagina, totalPaginasCot);
  const paginadas       = filas.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Modal comentario cierre — Ganada / Perdida / Rechazada */}
      {modalComent && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="modal-content" style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 460, width: "90%" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
              {modalComent.nuevoEstado === "Ganada"   ? "🎉 Marcar como Ganada"
             : modalComent.nuevoEstado === "Aprobada" ? "✅ Marcar como Aprobada"
             : modalComent.nuevoEstado === "Perdida"  ? "❌ Marcar como Perdida"
             : "🚫 Marcar como Rechazada"}
            </h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
              Cliente: <strong>{modalComent.nombre}</strong>
              {modalComent.numeroCot && <> · <strong>{modalComent.numeroCot}</strong></>}
            </p>

            {/* Motivo */}
            <label style={lbl}>
              {modalComent.nuevoEstado === "Ganada"   ? "¿Cómo se ganó?"
               : ["Aprobada"].includes(modalComent.nuevoEstado) ? "¿Qué proyecto se aprobó?"
               : modalComent.nuevoEstado === "Perdida" ? "¿Por qué se perdió?"
               : "¿Por qué fue rechazada?"}
            </label>
            <textarea value={comentario} onChange={e => setComentario(e.target.value)}
              placeholder={modalComent.nuevoEstado === "Ganada" ? "Ej: Cliente firmó contrato..." : "Ej: Eligieron a la competencia por precio..."}
              rows={3} style={{ ...inp, resize: "vertical", paddingTop: 8, paddingBottom: 8, marginBottom: 16 }} />

            {/* Próximo contacto con el cliente — para futuras oportunidades */}
            <div style={{ background: "#F8FAFF", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
              <label style={{ ...lbl, marginBottom: 4 }}>
                📅 ¿Cuándo volver a contactar al cliente? <span style={{ fontWeight: 400, color: "#94a3b8" }}>(opcional)</span>
              </label>
              <input type="date" value={proximoContactoCli} onChange={e => setProximoContactoCli(e.target.value)}
                style={{ ...inp, marginBottom: 4 }} />
              <div style={{ fontSize: 10, color: "#94a3b8" }}>
                Actualiza la agenda del cliente para futuras cotizaciones — la relación continúa.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirmarComent} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                background: ["Ganada","Aprobada"].includes(modalComent.nuevoEstado) ? "#1D9E75" : "#DC2626",
                color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}>
                Confirmar — {modalComent.nuevoEstado}
              </button>
              <button onClick={() => { setModalComent(null); setProximoContactoCli(""); }} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva / editar */}
      {modalNueva && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="modal-content" style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 20 }}>
              {modoEditar ? "✏ Editar cotización" : "Nueva cotización"}
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lbl}>N° Cotización</label>
                <input value={form.numeroCot} onChange={e => setForm(f => ({ ...f, numeroCot: e.target.value }))} placeholder="Ej: COT-2026-001" style={inp} />
              </div>
              <div>
                <label style={lbl}>Cliente *</label>
                <select value={form.clienteId} onChange={e => handleClienteChange(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre} — {c.empresa}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Proyecto / Descripción</label>
                <input value={form.proyecto} onChange={e => setForm(f => ({ ...f, proyecto: e.target.value }))} placeholder="Ej: Edificio Residencial Torres del Norte" style={inp} />
              </div>
              <div>
                <label style={lbl}>Valor</label>
                <input type="number" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} placeholder="Ej: 5000" style={inp} />
              </div>
              <div>
                <label style={lbl}>Estado</label>
                <select value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} style={{ ...inp, cursor: "pointer" }}>
                  {ESTADOS.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Responsable</label>
                <select value={form.responsable} onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={{ ...inp, cursor: "pointer" }}>
                  <option value="">— Seleccionar asesor —</option>
                  {(usuarios || []).filter(u => u.activo !== false).map(u => <option key={u.id} value={u.nombre}>{u.nombre}</option>)}
                </select>
              </div>
              {!ESTADOS_SIN_SEGUIMIENTO_PROPIO.includes(form.estado) && (
                <div>
                  <label style={lbl}>Próximo seguimiento</label>
                  <input type="date" value={form.proximoSeguimiento} onChange={e => setForm(f => ({ ...f, proximoSeguimiento: e.target.value }))} style={inp} />
                </div>
              )}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Notas</label>
                <textarea value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                  rows={2} placeholder="Observaciones..." style={{ ...inp, resize: "vertical", paddingTop: 8, paddingBottom: 8 }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleGuardar} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: "#3A3C7A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {modoEditar ? "Guardar cambios" : "Crear cotización"}
              </button>
              <button onClick={() => { setModalNueva(false); setModoEditar(false); setEditCotId(null); }} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>
          Cotizaciones <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 16 }}>({filas.length})</span>
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportarExcel} style={{ background: "#fff", color: "#1D9E75", border: "1.5px solid #1D9E75", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: "pointer" }}>
            ↓ Excel
          </button>
          <button onClick={abrirModalNueva} style={{ background: "#F97316", color: "#000", border: "none", fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8, cursor: "pointer" }}>
            + Nueva cotización
          </button>
        </div>
      </div>

      {/* Fila 1 — Estado chips: filtro + KPI combinado */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {/* Chip Todos */}
        {(() => {
          const activo = filtroEstado === "Todos";
          return (
            <button onClick={() => { setFiltroEstado("Todos"); setPagina(1); }} style={{
              background: activo ? "#3A3C7A" : "#fff", color: activo ? "#fff" : "#475569",
              border: `1.5px solid ${activo ? "#3A3C7A" : "#e2e8f0"}`, borderRadius: 12,
              padding: "12px 6px", cursor: "pointer", textAlign: "center",
              transition: "all 160ms ease", boxShadow: activo ? "0 2px 8px rgba(58,60,122,0.25)" : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Todos</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{cotizaciones.length}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}>{fmtMoney(kpis.totalValor)}</div>
            </button>
          );
        })()}
        {/* Chips por estado */}
        {ESTADOS.map(e => {
          const s = ESTADO_STYLE[e];
          const activo = filtroEstado === e;
          return (
            <button key={e} onClick={() => { setFiltroEstado(e); setPagina(1); }} style={{
              background: activo ? s.color : s.bg, color: activo ? "#fff" : s.color,
              border: `1.5px solid ${activo ? s.color : "transparent"}`,
              borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center",
              transition: "all 160ms ease", boxShadow: activo ? `0 2px 8px ${s.color}44` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: activo ? 0.9 : 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>{e}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{kpis.porEstado[e] || 0}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>{fmtMoney(kpis.porEstadoValor[e] || 0)}</div>
            </button>
          );
        })}
      </div>

      {/* Fila 2 — Métricas de performance */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {[
          { label: "Pipeline activo",    valor: fmtMoney(kpis.pipelineVal), sub: `${kpis.pipelineCnt} cotizaciones en proceso`, color: "#D97706" },
          { label: "Tasa de cierre real", valor: `${kpis.tasaCierre}%`,      sub: `sobre ${kpis.terminados} cotizaciones terminadas`,  color: "#1D9E75" },
          { label: "Ticket promedio",     valor: fmtMoney(kpis.ticketProm),  sub: "por cotización ganada",                         color: "#8B5CF6" },
        ].map(k => (
          <div key={k.label} className="kpi-card card-lift" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderTop: `3px solid ${k.color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 500, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.4px" }}>{k.valor}</div>
            <div style={{ fontSize: 10, marginTop: 4, color: k.color, fontWeight: 500 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtros — búsqueda + responsable */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Buscar por cliente, proyecto, N° cotización..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none" }}
        />
        {responsables.length > 0 && (
          <select value={filtroResponsable} onChange={e => setFiltroResponsable(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#475569", background: "#fff", cursor: "pointer" }}>
            <option value="Todos">Responsable: Todos</option>
            {responsables.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        {hayFiltros && (
          <button onClick={() => { setBusqueda(""); setFiltroEstado("Todos"); setFiltroResponsable("Todos"); setPagina(1); }}
            style={{ padding: "5px 10px", border: "none", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", cursor: "pointer", fontWeight: 600 }}>
            × Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <tr>
                <th style={TH}>N° Cotización</th>
                <th style={TH}>Cliente</th>
                <th style={TH}>Proyecto</th>
                <th onClick={() => handleSort("estado")} style={THS}>Estado <SortIcon field="estado" sortField={sortField} sortDir={sortDir} /></th>
                <th onClick={() => handleSort("valor")} style={THS}>Valor <SortIcon field="valor" sortField={sortField} sortDir={sortDir} /></th>
                <th style={TH}>Asesor</th>
                <th onClick={() => handleSort("seguimiento")} style={THS}>Seguim. <SortIcon field="seguimiento" sortField={sortField} sortDir={sortDir} /></th>
                <th style={TH}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>
                  No hay cotizaciones. Crea la primera con el botón de arriba.
                </td></tr>
              ) : paginadas.map(c => {
                const dias    = diasHasta(c.proximoSeguimiento);
                const estilo  = ESTADO_STYLE[c.estado] || ESTADO_STYLE["Nueva"];
                const isExp   = expandedId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr
                      style={{ borderBottom: "0.5px solid #f1f5f9", cursor: "pointer", background: isExp ? "#f8fafc" : "transparent" }}
                      onClick={() => setExpandedId(isExp ? null : c.id)}
                    >
                      {/* N° Cotización */}
                      <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                        {editNumId === c.id ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input value={editNumVal} onChange={e => setEditNumVal(e.target.value)}
                              style={{ width: 120, padding: "4px 8px", border: "1px solid #3A3C7A", borderRadius: 5, fontSize: 12 }}
                              autoFocus onKeyDown={e => { if (e.key === "Enter") guardarNumero(c.id); if (e.key === "Escape") setEditNumId(null); }} />
                            <button onClick={() => guardarNumero(c.id)} style={{ fontSize: 10, padding: "3px 7px", borderRadius: 4, background: "#3A3C7A", color: "#fff", border: "none", cursor: "pointer" }}>✓</button>
                            <button onClick={() => setEditNumId(null)} style={{ fontSize: 10, padding: "3px 6px", borderRadius: 4, background: "#f1f5f9", color: "#475569", border: "none", cursor: "pointer" }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "2px 7px", borderRadius: 5 }}>
                              {c.numeroCot || "—"}
                            </span>
                            <button onClick={() => { setEditNumId(c.id); setEditNumVal(c.numeroCot || ""); }}
                              style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#f1f5f9", color: "#64748b", border: "none", cursor: "pointer" }}>✏</button>
                          </div>
                        )}
                      </td>

                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{c.clienteNombre || "—"}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{c.clienteEmpresa || ""}</div>
                      </td>

                      <td style={{ padding: "10px 12px", color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.proyecto || "—"}
                      </td>

                      {/* Estado + días en estado / antigüedad vencida */}
                      <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                        <select value={c.estado || "Nueva"} onChange={e => handleCambiarEstado(c, e.target.value)}
                          style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 8, border: `1px solid ${estilo.color}`, background: estilo.bg, color: estilo.color, cursor: "pointer", outline: "none" }}>
                          {ESTADOS.map(e => <option key={e}>{e}</option>)}
                        </select>
                        {c.estado === "Vencida" && c.fechaVencimiento ? (() => {
                          const [y, m, d] = c.fechaVencimiento.split("-").map(Number);
                          const hoy = new Date(); hoy.setHours(0,0,0,0);
                          const dias = Math.round((hoy - new Date(y, m-1, d)) / 86400000);
                          return <div style={{ fontSize: 9, fontWeight: 700, color: "#854F0B", marginTop: 2 }}>Vencida hace {dias}d</div>;
                        })() : (() => { const d = diasEnEstado(c); return d !== null ? <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>{d}d en este estado</div> : null; })()}
                      </td>

                      <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1e293b" }}>
                        {fmtValor(c)}
                        {c.moneda === "USD" && (
                          <span style={{ marginLeft: 5, fontSize: 9, background: "#EFF6FF", color: "#1D4ED8", fontWeight: 700, padding: "1px 5px", borderRadius: 6 }}>USD</span>
                        )}
                      </td>

                      <td style={{ padding: "10px 12px", color: "#64748b", fontSize: 12 }}>{c.responsable || "—"}</td>

                      {/* Seguimiento — editable inline para estados activos y Vencida */}
                      <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                        {ESTADOS_SIN_SEGUIMIENTO_PROPIO.includes(c.estado) ? (
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
                        ) : editFechaId === c.id ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input
                              type="date" value={editFechaVal}
                              onChange={e => setEditFechaVal(e.target.value)}
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === "Enter") { actualizarCotizacion(c.id, { proximoSeguimiento: editFechaVal }); setEditFechaId(null); }
                                if (e.key === "Escape") setEditFechaId(null);
                              }}
                              style={{ padding: "3px 6px", border: "1px solid #3A3C7A", borderRadius: 5, fontSize: 11, width: 120 }}
                            />
                            <button onClick={() => { actualizarCotizacion(c.id, { proximoSeguimiento: editFechaVal }); setEditFechaId(null); }}
                              style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#3A3C7A", color: "#fff", border: "none", cursor: "pointer" }}>✓</button>
                            <button onClick={() => setEditFechaId(null)}
                              style={{ fontSize: 10, padding: "2px 5px", borderRadius: 4, background: "#f1f5f9", color: "#475569", border: "none", cursor: "pointer" }}>✕</button>
                          </div>
                        ) : (
                          <div
                            onClick={() => { setEditFechaId(c.id); setEditFechaVal(c.proximoSeguimiento || ""); }}
                            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                            title="Click para editar fecha"
                          >
                            <span style={{ fontWeight: 600, color: colorDias(dias), fontSize: 12 }}>{labelDias(dias)}</span>
                            <span style={{ fontSize: 9, color: "#cbd5e1" }}>✏</span>
                          </div>
                        )}
                      </td>

                      <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          {/* Botón crear pedido — cotizaciones Ganadas o Cerradas (legacy) */}
                          {["Ganada", "Cerrada", "Aprobada"].includes(c.estado) && (
                            <button
                              onClick={() => {
                                const notasPedido = [c.proyecto, c.notas].filter(Boolean).join(" · ");
                                navigate(`/pedidos?cotId=${c.id}&cotNum=${encodeURIComponent(c.numeroCot||"")}&clienteId=${c.clienteId||""}&clienteNombre=${encodeURIComponent(c.clienteNombre||"")}&clienteEmpresa=${encodeURIComponent(c.clienteEmpresa||"")}&valor=${c.valor||""}&notas=${encodeURIComponent(notasPedido)}`);
                              }}
                              style={{ fontSize: 11, background: "#E1F5EE", color: "#0F6E56", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              → Pedido
                            </button>
                          )}
                          {c.estado === "Vencida" && (
                            <button
                              onClick={() => handleCambiarEstado(c, "Perdida")}
                              style={{ fontSize: 11, background: "#FCEBEB", color: "#A32D2D", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              → Perdida
                            </button>
                          )}
                          <button onClick={() => abrirModalEditar(c)} style={{ fontSize: 11, background: "#E6F1FB", color: "#185FA5", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600 }}>
                            Editar
                          </button>
                          {confirmDel === c.id ? (
                            <>
                              <button onClick={() => { eliminarCotizacion(c.id); setConfirmDel(null); }} style={{ fontSize: 11, background: "#DC2626", color: "#fff", padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600 }}>Sí</button>
                              <button onClick={() => setConfirmDel(null)} style={{ fontSize: 11, background: "#f1f5f9", color: "#475569", padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer" }}>No</button>
                            </>
                          ) : (
                            permisos.puedeEliminar && (
                              <button onClick={() => setConfirmDel(c.id)} style={{ fontSize: 11, background: "#FCEBEB", color: "#A32D2D", padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontWeight: 600 }}>Del</button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Fila expandida — notas + comentarios de seguimiento */}
                    {isExp && (
                      <tr>
                        <td colSpan={8} style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "14px 20px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 20 }}>
                            {/* Notas generales */}
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                                Nota general de la cotización
                              </div>
                              <p style={{ fontSize: 12, color: "#475569", margin: "0 0 12px 0", lineHeight: 1.5 }}>
                                {c.notas || <em style={{ color: "#94a3b8" }}>Sin notas. Edita la cotización para agregar.</em>}
                              </p>
                              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                                {c.responsable && <div><div style={{ fontSize: 9, color: "#94a3b8" }}>Responsable</div><div style={{ fontSize: 11, fontWeight: 600, color: "#1e293b" }}>{c.responsable}</div></div>}
                                {c.proximoSeguimiento && !ESTADOS_SIN_SEGUIMIENTO_PROPIO.includes(c.estado) && <div><div style={{ fontSize: 9, color: "#94a3b8" }}>Próx. seguimiento</div><div style={{ fontSize: 11, color: colorDias(dias), fontWeight: 600 }}>{c.proximoSeguimiento}</div></div>}
                                {c.ultimoContacto && <div><div style={{ fontSize: 9, color: "#94a3b8" }}>Último contacto</div><div style={{ fontSize: 11, color: "#475569" }}>{c.ultimoContacto}</div></div>}
                              </div>
                            </div>
                            {/* Comentarios de seguimiento */}
                            <NotasSection
                              cotizacionId={c.id}
                              cotizacionNum={c.numeroCot}
                              clienteNombre={c.clienteNombre}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <BarraPaginas total={filas.length} porPagina={POR_PAGINA} pagina={paginaValida} onChange={setPagina} />
      </div>
    </div>
  );
}

function BarraPaginas({ total, porPagina, pagina, onChange }) {
  if (!total || total <= porPagina) return null;
  const totalPags = Math.ceil(total / porPagina);
  const desde = (pagina - 1) * porPagina + 1;
  const hasta  = Math.min(pagina * porPagina, total);
  const nums = [];
  for (let i = 1; i <= totalPags; i++) {
    if (i === 1 || i === totalPags || (i >= pagina - 1 && i <= pagina + 1)) nums.push(i);
    else if (nums[nums.length - 1] !== "...") nums.push("...");
  }
  const btnBase = { padding: "5px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer" };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 12, color: "#64748b" }}>Mostrando {desde} a {hasta} de {total} registros</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onChange(pagina - 1)} disabled={pagina === 1}
          style={{ ...btnBase, color: pagina === 1 ? "#d1d5db" : "#475569", cursor: pagina === 1 ? "not-allowed" : "pointer" }}>
          Anterior
        </button>
        {nums.map((n, i) => n === "..." ? (
          <span key={`e${i}`} style={{ fontSize: 12, color: "#94a3b8", padding: "0 2px" }}>…</span>
        ) : (
          <button key={n} onClick={() => onChange(n)} style={{
            width: 32, height: 32, borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer",
            borderColor: pagina === n ? "#3A3C7A" : "#e2e8f0",
            background:  pagina === n ? "#3A3C7A" : "#fff",
            color:       pagina === n ? "#fff"    : "#475569",
          }}>{n}</button>
        ))}
        <button onClick={() => onChange(pagina + 1)} disabled={pagina === totalPags}
          style={{ ...btnBase, color: pagina === totalPags ? "#d1d5db" : "#475569", cursor: pagina === totalPags ? "not-allowed" : "pointer" }}>
          Siguiente
        </button>
      </div>
    </div>
  );
}
